import {
  useRoute,
  useRouter,
} from '@nuxtjs/composition-api';
import differenceWith from 'lodash-es/differenceWith';
import isEqual from 'lodash-es/isEqual';
import pick from 'lodash-es/pick';
import { computed } from 'vue';

import type { WritableComputedRef } from 'vue';
import type { Location } from 'vue-router';

export type QueryFilterRawValue = string | Array<string | null>;

export type QueryFilterValues = Record<string, unknown>;

export interface QueryFiltersDiff {
  isChanged: boolean;
  isDebounce: boolean;
}

export interface UseQueryFiltersOptions {
  routeName?: string;
  watchedKeys: Array<string>;
  debounceKey?: string;
  normalizeQueryEntry? (key: string, value: QueryFilterRawValue): unknown;
}

export interface UseQueryFiltersReturn<TFilters extends QueryFilterValues = QueryFilterValues> {
  filters: WritableComputedRef<TFilters>;
  filtersChange (value: TFilters): void;
  getFiltersDiff (value: TFilters, oldValue: TFilters): QueryFiltersDiff;
}

export function useQueryFilters<TFilters extends QueryFilterValues = QueryFilterValues> (
  options: UseQueryFiltersOptions,
): UseQueryFiltersReturn<TFilters> {
  const {
    routeName,
    watchedKeys,
    debounceKey = 'search',
    normalizeQueryEntry = (key: string, value: QueryFilterRawValue): unknown => value,
  } = options;

  const route = useRoute();
  const router = useRouter();

  const filters = computed<TFilters>({
    get () {
      return Object
        .entries(route.value.query)
        .reduce<QueryFilterValues>((accumulator, [key, value]) => {
          if (!value) {
            return accumulator;
          }

          const normalized = normalizeQueryEntry(key, value);

          if (normalized !== undefined) {
            accumulator[key] = normalized;
          }

          return accumulator;
        }, {}) as TFilters;
    },

    set (values) {
      const reduced = Object
        .entries(values)
        .reduce<QueryFilterValues>((accumulator, [key, value]) => {
          accumulator[key] = value || undefined;

          return accumulator;
        }, {});

      router.push({
        name: routeName ?? route.value.name ?? undefined,
        query: {
          ...route.value.query,
          ...reduced,
        } as Location['query'],
      }).catch(() => undefined);
    },
  });

  function filtersChange (value: TFilters): void {
    filters.value = value;
  }

  function getFiltersDiff (value: TFilters, oldValue: TFilters): QueryFiltersDiff {
    const isChanged = !isEqual(pick(value, watchedKeys), pick(oldValue, watchedKeys));

    if (!isChanged) {
      return {
        isChanged,
        isDebounce: false,
      };
    }

    const newEntries = Object.entries(value);
    const oldEntries = Object.entries(oldValue);
    const diff = differenceWith(newEntries, oldEntries, isEqual);
    const isDebounce = Boolean(diff.length && diff[0]?.length && diff[0][0] === debounceKey);

    return {
      isChanged,
      isDebounce,
    };
  }

  return {
    filters,
    filtersChange,
    getFiltersDiff,
  };
}
